// ---------------------------------------------------------------------------
// Fixed physical calibration ranges, one per parameter in
// baseTemplate.ts's `PARAMETER_DEFINITIONS` — the user's own explicit
// choice for this feature ("Rangos físicos fijos por parámetro/modelo", not
// user-editable, see project_evalhid_calibracion.md): CalibrationPanel.tsx
// only lets the user pick WHICH parameters to calibrate, never their
// search range.
//
// No such table existed anywhere in this codebase before (confirmed:
// `ParameterDefinition`/`PARAMETER_DEFINITIONS` only carry a single
// `defaultValue`, no bounds) — these are derived from the standard
// calibration literature for each model (Témez/CEDEX; Burnash 1973/NWS-RFC
// calibration guidelines for SAC-SMA; Bergström 1992/Seibert 1997 for HBV;
// Perrin 2003/airGR for GR2M+GR4J; Boughton 2004 for AWBM), deliberately
// wide enough to always contain this app's own default value (see the
// assertion in test/calibrationParameterRanges.test.ts) — they're a search
// box for SCE-UA, not a strict physical constraint, so erring wide is safe
// (slower convergence, never a wrong answer) and erring narrow is not
// (could exclude the true optimum entirely).
// ---------------------------------------------------------------------------

import type { ParameterDefinition } from "./types.ts";

interface ParameterRange {
  min: number;
  max: number;
}

function key(modelTypeCode: number, kind: number, parameterCode: number): string {
  return `${modelTypeCode}-${kind}-${parameterCode}`;
}

const RANGES: Record<string, ParameterRange> = {
  // Témez (1/0)
  [key(1, 0, 101)]: { min: 50, max: 500 }, // Hmax
  [key(1, 0, 102)]: { min: 0, max: 1 }, // C
  [key(1, 0, 103)]: { min: 0, max: 600 }, // Imax
  [key(1, 0, 104)]: { min: 0.001, max: 1 }, // Alfa

  // SAC (2/0)
  [key(2, 0, 201)]: { min: 1, max: 150 }, // Uztwm
  [key(2, 0, 202)]: { min: 1, max: 150 }, // Uzfwm
  [key(2, 0, 203)]: { min: 0.1, max: 0.5 }, // Uzk
  [key(2, 0, 204)]: { min: 0, max: 0.1 }, // Pctim
  [key(2, 0, 205)]: { min: 0, max: 0.4 }, // Adimp
  [key(2, 0, 206)]: { min: 0, max: 0.2 }, // Riva
  [key(2, 0, 207)]: { min: 1, max: 100 }, // Zperc
  [key(2, 0, 208)]: { min: 1, max: 5 }, // Rexp
  [key(2, 0, 209)]: { min: 1, max: 500 }, // Lztwm
  [key(2, 0, 210)]: { min: 1, max: 400 }, // Lzfsm
  [key(2, 0, 211)]: { min: 1, max: 1000 }, // Lzfpm
  [key(2, 0, 212)]: { min: 0.01, max: 0.35 }, // Lzsk
  [key(2, 0, 213)]: { min: 0.0001, max: 0.025 }, // Lzpk
  [key(2, 0, 214)]: { min: 0, max: 0.6 }, // Pfree
  [key(2, 0, 215)]: { min: 0, max: 0.3 }, // Side
  [key(2, 0, 216)]: { min: 0, max: 0.2 }, // Saved

  // HBV (3/0)
  [key(3, 0, 301)]: { min: 1, max: 6 }, // Beta
  [key(3, 0, 302)]: { min: 50, max: 500 }, // FC
  [key(3, 0, 303)]: { min: 0, max: 500 }, // Pwp
  [key(3, 0, 304)]: { min: 0, max: 100 }, // Lmax
  [key(3, 0, 305)]: { min: 0.05, max: 0.5 }, // K0
  [key(3, 0, 306)]: { min: 0.01, max: 0.3 }, // K1
  [key(3, 0, 307)]: { min: 0.001, max: 0.5 }, // K2 (default 0.2 > K0's default 0.13, see baseTemplate.ts's own note)
  [key(3, 0, 308)]: { min: 0.001, max: 0.5 }, // Kperc

  // GR2M (4/0)
  [key(4, 0, 401)]: { min: 1, max: 3000 }, // X1
  [key(4, 0, 402)]: { min: 0, max: 3 }, // X2

  // GR4J (5/0)
  [key(5, 0, 501)]: { min: 1, max: 2000 }, // X1
  [key(5, 0, 502)]: { min: -5, max: 5 }, // X2
  [key(5, 0, 503)]: { min: 1, max: 500 }, // X3
  [key(5, 0, 504)]: { min: 0.5, max: 10 }, // X4

  // AWBM (6/0)
  [key(6, 0, 601)]: { min: 1, max: 500 }, // Cave
  [key(6, 0, 602)]: { min: 0, max: 1 }, // Bfi
  [key(6, 0, 603)]: { min: 0, max: 1 }, // Ksurf
  [key(6, 0, 604)]: { min: 0, max: 1 }, // Kb

  // Nieve1 (1/1)
  [key(1, 1, 101)]: { min: -3, max: 3 }, // TT
  [key(1, 1, 102)]: { min: 0.1, max: 20 }, // DD

  // Nieve2 (2/1)
  [key(2, 1, 201)]: { min: 0.1, max: 10 }, // An
  [key(2, 1, 202)]: { min: 0.0001, max: 1 }, // Bp
  [key(2, 1, 203)]: { min: 0, max: 1 }, // Ocr
  [key(2, 1, 204)]: { min: -5, max: 5 }, // Tcp1
  [key(2, 1, 205)]: { min: 0, max: 10 }, // Tcp2
  [key(2, 1, 206)]: { min: -5, max: 5 }, // Tcr
};

/** Fixed search range for a calibratable parameter — `undefined` for a definition this table doesn't (yet) cover. */
export function calibrationRangeFor(def: ParameterDefinition): ParameterRange | undefined {
  return RANGES[key(def.modelTypeCode, def.kind, def.parameterCode)];
}
